import { instrumentDurations, ownPeriodComparison, practiceDuration, weeklyConsistency, type PracticeMetricsSummary, type PracticeOwnComparison } from "@wmf/shared/practiceMetrics";
import { get } from "../api.js";
import { useAsync } from "../useAsync.js";

const label = (instrument: string | null) => (instrument ? instrument[0]!.toUpperCase() + instrument.slice(1) : "Instrument not recorded");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
// Fixed month names: `Intl` abbreviates September differently between ICU versions.
const shortDate = (day: string) => `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1]}`;

function Comparison({ unit, comparison }: { unit: "week" | "month"; comparison: PracticeOwnComparison }) {
  const ahead = comparison.differenceSeconds > 0;
  return (
    <p>
      <strong className="metric-number">{practiceDuration(comparison.currentSeconds)}</strong> saved this {unit}. By the same point last {unit} you had saved{" "}
      <strong className="metric-number">{practiceDuration(comparison.previousSeconds)}</strong>.
      {ahead ? ` That’s ${practiceDuration(comparison.differenceSeconds)} more so far. Keep it up!` : ""}
    </p>
  );
}

/**
 * Personal practice insights for one learner. Every figure is read from the
 * existing practice-metrics summary, so it matches Practice History for the
 * same learner, period and instrument filter, and is derived by the shared
 * helpers rather than recalculated here. It never compares a learner with
 * anyone else and says nothing about ability.
 */
export function PracticeInsights({ base, instrument, today, refresh = 0 }: { base: string; instrument: string; today: string; refresh?: number }) {
  const state = useAsync(async () => {
    const params = { on: today, ...(instrument ? { instrument } : {}) };
    const [week, month] = await Promise.all([
      get<PracticeMetricsSummary>(`${base}/summary`, { ...params, period: "week" }),
      get<PracticeMetricsSummary>(`${base}/summary`, { ...params, period: "month" }),
    ]);
    return { week, month };
  }, [base, instrument, today, refresh]);

  const heading = (
    <>
      <h2 id="metric-insights-title">Your practice at a glance</h2>
      <p className="small muted">Based only on practice you have saved{instrument ? ` on ${label(instrument).toLowerCase()}` : ""}. Not compared with anyone else.</p>
    </>
  );

  if (state.loading && !state.data) {
    return (
      <section className="panel metric-insights" aria-labelledby="metric-insights-title">
        {heading}
        <p role="status">Loading your insights…</p>
      </section>
    );
  }
  if (!state.data) {
    return (
      <section className="panel metric-insights" aria-labelledby="metric-insights-title">
        {heading}
        <p role="alert">
          Your insights could not be loaded.{" "}
          <button className="quiet small" type="button" onClick={state.reload}>
            Retry
          </button>
        </p>
      </section>
    );
  }

  const { week, month } = state.data;
  const weeks = weeklyConsistency(month);
  const instruments = instrumentDurations(month);
  return (
    <section className="panel metric-insights" aria-labelledby="metric-insights-title">
      {heading}
      {month.totals.totalSeconds === 0 && week.totals.totalSeconds === 0 ? (
        <p>Nothing saved yet this month. Practice you save will show up here.</p>
      ) : (
        <>
          <h3>This week</h3>
          <Comparison unit="week" comparison={ownPeriodComparison(week)} />
          <h3>This month</h3>
          <Comparison unit="month" comparison={ownPeriodComparison(month)} />
          <h3>Days you practised each week this month</h3>
          <ul>
            {weeks.map((entry) => (
              <li key={entry.weekStart}>
                Week of {shortDate(entry.weekStart)}: <strong>{entry.daysPracticed}</strong> {entry.daysPracticed === 1 ? "day" : "days"}
                {entry.totalSeconds > 0 ? <span className="small muted"> · {practiceDuration(entry.totalSeconds)}</span> : null}
              </li>
            ))}
          </ul>
          {!instrument && instruments.length > 0 && (
            <>
              <h3>By instrument this month</h3>
              <dl>
                {instruments.map((entry) => (
                  <div className="spread" key={entry.instrument ?? "unclassified"}>
                    <dt>{label(entry.instrument)}</dt>
                    <dd className="metric-number">{practiceDuration(entry.totalSeconds)}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </>
      )}
    </section>
  );
}
